import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getI18n, useTranslation } from 'react-i18next';
import i18n from 'resources/i18n';
import moment from 'moment';

import { useGetShipInfoQuery } from 'app/api/ships';
import {
  useGetMapSnapshotMutation,
  useGetNearbyShipsMutation,
  useGetShipNoteQuery,
  useLazyGetHistoryQuery,
  useGetVesselsGeoInfoMutation,
} from 'app/api/facade';
import { useOnClickOutside, useVesselCharacteristics, useFormatMoment, useGetDestinationInfo } from 'app/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { changePredictedRouteVisibility, setPredictedRoute, setRouteForSelectedVessel } from 'app/utils/selectedTargetsSlice';
import { SnapshotData } from 'ais-facade-api/api';
import {
  AISCardHeader,
  AccordionComponent,
  Icon,
  InfoWrapper,
  NearbyShipsColumns,
  VesselPhoto,
  VesselProgressBar,
  VesselsCatalogModal,
  ShipNote,
  ShipNoteModal,
  DotsLoader,
} from 'app/components';
import { CountryFlag } from 'CountryFlag/CountryFlag';
import { glToStringHDMS } from 'app/helpers';

import styles from './AISCard.module.scss';
import { toggleIsVesselPlaybackActive } from 'app/utils/mapStateSlice';

type AISCardStateT = {
  isHistoryMode?: boolean;
  shown?: boolean;
  className?: string;
  onClose: () => void;
  mmsi: number;
  setShowCurRoute?: React.Dispatch<
    React.SetStateAction<{
      mmsi?: number | undefined;
      show: boolean;
    }>
  >;
  showCurRoute?: { mmsi?: number; show: boolean };
  onClick?: () => void;
  snapshotData?: SnapshotData;
  showRoute?: boolean;
  setShowRoute?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AISCard = ({ mmsi, shown, onClose, onClick, snapshotData, isHistoryMode }: AISCardStateT) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const route = useAppSelector((state) => state.selectedTargetsSlice.route);
  const routePredicted = useAppSelector((state) => state.selectedTargetsSlice.predictedRoute);
  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);
  const profile = useAppSelector((state) => state.userSlice.profile);
  const isVesselPlaybackActive = useAppSelector((state) => state.mapStateSlice.isVesselPlaybackActive);

  const { data: ship, ...shipInfoRequest } = useGetShipInfoQuery(mmsi, { skip: isHistoryMode });
  const { isLoading: isNotesLoading } = useGetShipNoteQuery(mmsi, { skip: typeof mmsi !== 'number' });

  const isNotBaseMode = mode === 'analytics' || mode === 'playback' || mode === 'history';

  const vesselsInTNearbyZone = useGetNearbyShipsMutation();
  const [getVesselAreasInfo, { ...getVesselAreasInfoResponse }] = useGetVesselsGeoInfoMutation();
  const [, snapshotRequest] = useGetMapSnapshotMutation({
    fixedCacheKey: 'get-map-snapshot-mutation',
  });

  const language = useMemo(() => getI18n().language, [t]);
  const timeFormat = useMemo(() => profile?.ui?.['timeFormat'] || 'HH:mm:ss', [profile]);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  const isDataOld = moment().diff(moment(moment(ship?.result?.t)), 'second') >= 3600;
  const [showNearbyVessels, setShowNearbyVessels] = useState<boolean>(false);

  const showNoteModalState = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const clickOutsideHandler = () => {
    setShowNearbyVessels(false);
    showNoteModalState[1](false);
  };
  useOnClickOutside(modalRef, clickOutsideHandler);

  const [loadHistory] = useLazyGetHistoryQuery();

  const isLoading = shipInfoRequest.isLoading || shipInfoRequest.isFetching;

  const formatMoment = useFormatMoment();

  const handleDownload = useCallback(() => {
    loadHistory(mmsi)
      .unwrap()
      .then(({ url, filename }) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
  }, [mmsi]);

  const handleShowRoute = useCallback(() => {
    if (route.isShown) {
      dispatch(setRouteForSelectedVessel({ isShown: false, route: [], routePoints: [] }));
    } else {
      dispatch(setRouteForSelectedVessel({ ...route, isShown: !route.isShown }));
    }
  }, [route]);

  const position: string | undefined = useMemo(() => {
    if (snapshotData && typeof snapshotData.lat === 'number' && typeof snapshotData.lon === 'number') {
      return glToStringHDMS([snapshotData.lon, snapshotData.lat], coordinatesFormat);
    }

    if (typeof ship?.result?.position?.latitude === 'number' && typeof ship?.result?.position?.longitude === 'number') {
      return glToStringHDMS([ship.result.position.longitude, ship.result.position.latitude], coordinatesFormat);
    } else {
      return undefined;
    }
  }, [ship?.result?.position, snapshotData, coordinatesFormat]);

  const course = useMemo(() => {
    if (snapshotData) {
      return snapshotData.cog;
    }
    if (ship?.result && ship.result.position) {
      return ship.result.position.cog;
    }
  }, [snapshotData, ship?.result]);

  const speed: number | undefined = useMemo(() => {
    if (snapshotData) {
      return snapshotData.sog;
    }
    if (ship?.result && typeof ship.result?.position?.sog === 'number') {
      if (ship.result.position.sog === 0) return 0;
      return Number(ship.result.position.sog.toFixed(2));
    }
  }, [snapshotData, ship?.result]);

  const status = useMemo(() => {
    if (snapshotData) {
      return snapshotData.meta?.status;
    }
    if (ship?.result && ship.result.meta?.status) {
      return ship.result.meta?.status;
    }
  }, [snapshotData, ship?.result]);

  const heading = useMemo(() => {
    if (snapshotData) {
      return snapshotData.heading;
    }
    if (ship?.result && ship.result.position?.heading) {
      return ship.result.position.heading;
    }
  }, [snapshotData, ship?.result]);

  const { lengthDimension, widthDimension, withDeadWeight, withGrossTonnage } = useVesselCharacteristics(
    snapshotData || ship?.result,
  );

  const lastUpload = useMemo(() => {
    let dataResource;
    if (snapshotData && snapshotData.t !== undefined) {
      dataResource = snapshotData.t;
    }
    if (ship?.result?.t !== undefined) {
      dataResource = ship.result.t;
    }
    return formatMoment(moment(dataResource));
  }, [snapshotData, ship?.result, formatMoment]);

  const { eta, destination, calculatedETA, departureTime, departurePortName, departurePortKey, departurePortCode } =
    useGetDestinationInfo(ship);

  const departureTimeSplitted = departureTime?.split('–');
  const etaSplitted = eta?.split('–');
  const isETAInPast = moment(eta, `DD.MM.YYYY - ${timeFormat}`).utc().isBefore(moment().utc());
  const prediction = ship?.result?.destinationPorts;
  const source = ship?.result?.meta?.source;
  const safety = ship?.result?.safety;

  const handleRefetch = () => {
    if (isHistoryMode) {
      if (!snapshotRequest.isUninitialized) {
        snapshotRequest.reset();
      }
    } else {
      if (!shipInfoRequest.isUninitialized) {
        shipInfoRequest.refetch();
      }
    }
  };

  const getAreasToDisplay = () => {
    if (
      !getVesselAreasInfoResponse.isLoading &&
      getVesselAreasInfoResponse.isSuccess &&
      getVesselAreasInfoResponse.data &&
      getVesselAreasInfoResponse.data.results
    ) {
      const result = getVesselAreasInfoResponse.data.results;
      const areasNames = [] as string[];

      result.forEach((item) => {
        item.inAreas?.forEach((area) => {
          area.name ? areasNames.push(area.name + `,${' '}`) : null;
        });
      });
      return areasNames.length > 0 ? <div className={styles.geofence_info}>{areasNames}</div> : null;
    }
  };

  useEffect(() => {
    if (language && !shipInfoRequest.isUninitialized) {
      shipInfoRequest.refetch();
    }
  }, [language]);

  useEffect(() => {
    if (
      ship &&
      typeof ship.result?.mmsi === 'number' &&
      typeof ship.result?.position?.latitude === 'number' &&
      typeof ship.result?.position?.longitude === 'number'
    ) {
      getVesselAreasInfo({
        body: {
          points: [
            {
              key: mmsi.toString() || '',
              latitude: ship?.result?.position?.latitude,
              longitude: ship?.result?.position?.longitude,
            },
          ],
        },
        type: ['IN_AREA'],
      });
    }
  }, [ship]);

  return (
    <>
      {shown && ((ship?.result && !isLoading) || snapshotData) && !isNotesLoading && (
        <div className={styles.wrapper}>
          <AISCardHeader onClose={onClose} shipInfo={snapshotData || ship?.result} shipRequestRefetch={handleRefetch} />
          {typeof safety?.lastSignal === 'string' && (
            <div className={styles.safety_block} title={safety?.text}>
              {<span className={styles.label_icon}>{<Icon size="14px" name="safety" />}</span>}
              {t('Alarm')}
            </div>
          )}
          <div className={styles.content}>
            <ShipNote mmsi={snapshotData?.mmsi || ship?.result?.mmsi} />

            {!isHistoryMode && (
              <div className={styles.progress_bar}>
                {typeof eta === 'string' && departureTime && !isETAInPast ? (
                  <VesselProgressBar vessel={ship?.result} eta={eta} departureTime={departureTime} />
                ) : (
                  <VesselProgressBar empty />
                )}
              </div>
            )}
            <div className={styles.voyage_block}>
              <div className={styles.departure_block}>
                <div className={styles.port}>
                  {departurePortKey ? (
                    <>
                      {departurePortCode && <CountryFlag countryCode={departurePortCode.toLowerCase()} />}
                      <Link key={departurePortKey} className={styles.port_link} to={`/ports/${departurePortKey}`}>
                        {departurePortName || departurePortKey}
                      </Link>
                    </>
                  ) : (
                    <div className={styles.no_data}>&mdash;</div>
                  )}
                </div>
                {departureTime ? (
                  <div className={styles.time_wrapper}>
                    <strong>{departureTimeSplitted?.[0]}</strong>
                    <strong>{departureTimeSplitted?.[1]}</strong>
                  </div>
                ) : (
                  <div>
                    <strong className={styles.no_data}>&mdash;</strong>
                  </div>
                )}
              </div>
              <div className={styles.destination_block}>
                {!isHistoryMode && prediction && prediction?.length === 1 ? (
                  <>
                    <div className={styles.port}>
                      <Link key={prediction[0].key} className={styles.port_link} to={`/ports/${prediction[0].key}`}>
                        {prediction[0].name}
                      </Link>
                      {prediction[0].countryCode && <CountryFlag countryCode={prediction[0].countryCode.toLowerCase()} />}
                    </div>
                    {eta ? (
                      <div className={styles.time_wrapper}>
                        <strong>{etaSplitted?.[0]}</strong>
                        <strong>{etaSplitted?.[1]}</strong>
                      </div>
                    ) : (
                      <div>
                        <strong className={styles.no_data}>&mdash;</strong>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className={styles.no_data}>&mdash;</div>
                    <div className={styles.time}>
                      <div className={styles.no_data}>&mdash;</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {prediction && prediction?.length > 1 && (
              <InfoWrapper
                label={`${t('PredictedPort')}:`}
                clickable
                value={
                  <div className={styles.predict_list}>
                    {prediction.map((port: any) => {
                      return (
                        <Link key={port.key} className={styles.port_link} to={`/ports/${port.key}`}>
                          {port.name}
                        </Link>
                      );
                    })}
                  </div>
                }
              />
            )}

            {!isHistoryMode && (
              <InfoWrapper
                label={`${t('DestinationAis')}:`}
                value={ship?.result?.voyage && destination ? destination : undefined}
              />
            )}
            {!isHistoryMode && <InfoWrapper label={`${t('CalculatedETA')}:`} value={calculatedETA} strong />}

            <AccordionComponent label={`${t('Photo')}:`}>
              <div className={styles.photo_container}>
                <VesselPhoto imo={ship?.result?.registryData?.imo ?? ship?.result?.staticData?.imo} />
              </div>
            </AccordionComponent>
            <AccordionComponent label={`${t('CurrentPosition')}:`} aid={1} parent="ais_card">
              <div className={styles.course_wrapper}>
                <div className={styles.position_left}>
                  <div className={styles.label}>{`${t('Course')}:`}</div>
                  <div className={styles.course}>
                    {typeof course === 'number' ? <span>{course}&deg;</span> : <div className={styles.no_data}>&mdash;</div>}
                  </div>
                </div>
                <div className={styles.position_center}>
                  <div className={styles.label}>{`${t('GyroCompass')}:`}</div>
                  <div className={styles.heading}>
                    {typeof heading === 'number' ? <span>{heading}&deg;</span> : <div className={styles.no_data}>&mdash;</div>}
                  </div>
                </div>
                <div className={styles.position_right}>
                  <div className={styles.label}>{`${t('Speed')}:`}</div>
                  <div className={styles.speed}>
                    {typeof speed === 'number' ? (
                      `${speed} ${t('VesselSpeedUnit')}`
                    ) : (
                      <div className={styles.no_data}>&mdash;</div>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.status}>
                <div className={styles.label}>{`${t('NavStatus')}:`}</div>
                <div className={styles.value}>
                  {typeof status === 'string' ? <div>{status}</div> : <div className={styles.no_data}>&mdash;</div>}
                </div>
              </div>
              {typeof safety?.lastSignal === 'string' && (
                <div className={styles.safety_container}>
                  <InfoWrapper
                    labelIcon={<Icon size="14px" name="safety" />}
                    label={`${t('AlarmMessage')}:`}
                    title={safety?.text}
                    value={formatMoment(moment(safety.lastSignal))}
                  />
                </div>
              )}
              <InfoWrapper
                clickable={!!position}
                label={`${t('Position')}:`}
                value={position}
                valueIcon={<Icon name="aim" size="14px" color={'var(--default-primary)'} />}
                onClick={onClick}
              />
              {!isHistoryMode && (
                <InfoWrapper
                  label={`${t('Areas')}:`}
                  value={getVesselAreasInfoResponse.isLoading ? <DotsLoader /> : getAreasToDisplay()}
                />
              )}
              {source && (
                <InfoWrapper
                  label={`${source}:`}
                  value={lastUpload}
                  title={typeof ship?.result?.sourceCode === 'number' ? `${t('Source')}: ${ship.result.sourceCode}` : undefined}
                />
              )}
            </AccordionComponent>
            <AccordionComponent label={`${t('Registration')}:`} aid={2} parent="ais_card">
              {!isHistoryMode && (
                <InfoWrapper
                  label={`${t('Flag')}:`}
                  value={
                    typeof ship?.result?.meta?.countryName !== 'undefined' ? (
                      <>
                        {ship.result?.meta?.countryCode && (
                          <CountryFlag countryCode={ship.result.meta.countryCode.toLowerCase()} />
                        )}
                        <strong className={styles.countryName} title={ship.result?.meta?.countryName}>
                          {ship.result?.meta?.countryName} ({ship.result?.meta?.countryCode})
                        </strong>
                      </>
                    ) : null
                  }
                />
              )}
              <InfoWrapper label={`${t('MMSI')}:`} value={snapshotData?.mmsi || ship?.result?.mmsi} strong />
              {!isHistoryMode && (
                <InfoWrapper label={`${t('IMO')}:`} value={ship?.result?.registryData?.imo ?? ship?.result?.staticData?.imo} />
              )}
              <InfoWrapper
                label={`${t('CallSign')}:`}
                value={snapshotData?.callSign || (ship?.result?.registryData?.callSign ?? ship?.result?.staticData?.callSign)}
              />
            </AccordionComponent>
            <AccordionComponent label={`${t('ConstructionAndOperation')}:`} aid={3} parent="ais_card">
              <InfoWrapper
                label={`${t('CreationYear')}:`}
                value={
                  ship?.result?.registryData?.constructionDate
                    ? new Date(ship.result?.registryData?.constructionDate).getFullYear()
                    : undefined
                }
              />
              <InfoWrapper label={`${t('Shipbuilder')}:`} value={ship?.result?.registryData?.powerPlantType} />
              <InfoWrapper label={`${t('Owner')}:`} value={ship?.result?.registryData?.owner} />
              <div className={styles.class_symbol}>
                {t('SimbolOfClass')}: {ship?.result?.registryData?.soc ? <span>{ship?.result?.registryData?.soc}</span> : '—'}
              </div>
            </AccordionComponent>

            <AccordionComponent label={`${t('Design')}:`} aid={4} parent="ais_card">
              <InfoWrapper label={`${t('DeadWeight')}:`} value={withDeadWeight} />
              <InfoWrapper label={`${t('GrossTonnage')}:`} value={withGrossTonnage} />
              <InfoWrapper label={`${t('LengthDimension')}:`} value={lengthDimension} />
              <InfoWrapper label={`${t('WidthDimension')}:`} value={widthDimension} />
              <InfoWrapper
                label={`${t('Displacement')}:`}
                value={
                  typeof ship?.result?.registryData?.displacement === 'number'
                    ? `${ship?.result?.registryData?.displacement} ${i18n.t('WeightUnit')}`
                    : undefined
                }
              />
              {!snapshotData && (
                <InfoWrapper
                  label={`${t('Draught')}:`}
                  value={
                    typeof ship?.result?.voyage?.draught === 'number'
                      ? `${ship?.result?.voyage?.draught} ${t('MeterUnit')}`
                      : undefined
                  }
                />
              )}
            </AccordionComponent>
          </div>
          <div className={styles.actions}>
            <div className={styles.actions_block}>
              <Link to={`/vessels/${mmsi}`} title={t('Info')} className={styles.actions_icon}>
                <Icon name="vessel-info" color="var(--default-primary)"></Icon>
              </Link>
              <div
                title={t('ShipNote')}
                onClick={() => showNoteModalState[1](true)}
                data-active={showNoteModalState[0]}
                className={styles.actions_icon}
              >
                <Icon name="notes" />
              </div>
            </div>
            <div className={styles.actions_block}>
              {!isHistoryMode && position && !snapshotData && (
                <>
                  <div
                    title={t('NearbyShips')}
                    onClick={() => setShowNearbyVessels(!showNearbyVessels)}
                    aria-disabled={isDataOld}
                    data-active={showNearbyVessels}
                    className={styles.actions_icon}
                  >
                    <Icon className={styles.icon_radar} name="radar" />
                  </div>
                  <div
                    title={t('ShowRoutePredicted')}
                    onClick={() => {
                      dispatch(setPredictedRoute(null));
                      dispatch(changePredictedRouteVisibility(!routePredicted.isShown));
                    }}
                    aria-disabled={prediction?.length !== 1 || !uiConfig?.routeBuilderAvailable}
                    data-active={routePredicted.isShown}
                    className={styles.actions_icon}
                  >
                    <Icon className={styles.icon_predicted_route} name="predicted-route" />
                  </div>
                  <div
                    title={t('ShowRoute')}
                    onClick={handleShowRoute}
                    data-active={route.isShown}
                    className={styles.actions_icon}
                  >
                    <Icon name="vessel-track" />
                  </div>
                </>
              )}
              {!snapshotData && (
                <div onClick={handleDownload} className={styles.actions_icon} title={t('DownloadReport')}>
                  <Icon name="vessel-save-track" />
                </div>
              )}
              {!isNotBaseMode && (
                <div
                  title={t('VesselPlayback')}
                  onClick={() => dispatch(toggleIsVesselPlaybackActive())}
                  data-active={isVesselPlaybackActive}
                  className={styles.actions_icon}
                  aria-disabled={!uiConfig?.feSinglePlaybackAvailable}
                >
                  <Icon name="vessel-playback" />
                </div>
              )}
            </div>
          </div>
          {showNearbyVessels && ship?.result?.mmsi && (
            <VesselsCatalogModal
              titles={{
                title: 'NearbyShips',
                titleTotal: 'NearbyShips',
              }}
              requestParam={ship?.result?.mmsi}
              innerRef={modalRef}
              onClose={() => {
                setShowNearbyVessels(false);
              }}
              columns={NearbyShipsColumns}
              defaultSortBy={'DISTANCE'}
              defaultSortDirection={'ASC'}
              vesselsDataMutationResult={vesselsInTNearbyZone}
            />
          )}
        </div>
      )}
      <ShipNoteModal mmsi={snapshotData?.mmsi || ship?.result?.mmsi} modalState={showNoteModalState} />
    </>
  );
};
